import { db } from './database';
import { buildCampusCatalog } from '../data/populate';
import { CAMPUS_SCALE_PROFILE } from '../data/profile';
import type {
  CampusRepository,
  InventoryRepository,
  InfrastructureRepository,
  WorkRepository,
  IncidentRepository,
  VendorRepository,
  Room, Person, Department,
  Asset, AssetPage, AssetQuery, DeviceAssignment, CampusScaleSummary,
  DataJack, NetworkPath,
  WorkOrder, MaintenanceTask,
  Incident,
  Vendor, Service
} from '../domain';

let canonicalCatalog: ReturnType<typeof buildCampusCatalog> | null = null;

function getCanonicalCatalog() {
  if (!canonicalCatalog) {
    canonicalCatalog = buildCampusCatalog();
    canonicalCatalog.assets.sort((first, second) => first.id.localeCompare(second.id));
  }
  return canonicalCatalog;
}

function getCanonicalAssignment(assetId: string) {
  return getCanonicalCatalog().deviceAssignments.filter((assignment) => assignment.assetId === assetId);
}

export class LocalCampusRepository implements CampusRepository {
  async getRoom(roomId: string): Promise<Room | null> {
    return (await db.rooms.get(roomId)) || null;
  }
  async getPerson(personId: string): Promise<Person | null> {
    return (await db.persons.get(personId)) || null;
  }
  async getDepartment(departmentId: string): Promise<Department | null> {
    return (await db.departments.get(departmentId)) || null;
  }
}

export class LocalInventoryRepository implements InventoryRepository {
  async getAsset(assetId: string): Promise<Asset | null> {
    return (await db.assets.get(assetId))
      || getCanonicalCatalog().assets.find((asset) => asset.id === assetId)
      || null;
  }
  async getAllAssets(): Promise<Asset[]> {
    return getCanonicalCatalog().assets;
  }
  async getAssetPage(query: AssetQuery = {}): Promise<AssetPage> {
    const offset = Math.max(0, query.offset ?? 0);
    const limit = Math.min(100, Math.max(1, query.limit ?? 50));
    const normalizedQuery = query.query?.trim().toLocaleLowerCase();
    const catalog = getCanonicalCatalog().assets;
    const total = catalog.length;
    const filtered = catalog.filter((asset) => {
      if (query.state && asset.state !== query.state) return false;
      if (query.health && asset.health !== query.health) return false;
      if (query.category && asset.category !== query.category) return false;
      if (query.attentionOnly && asset.health === 'healthy') return false;
      if (!normalizedQuery) return true;
      return [
        asset.id,
        asset.name,
        asset.assetTag,
        asset.serialNumber,
        asset.category
      ].some((value) => value?.toLocaleLowerCase().includes(normalizedQuery));
    });

    const filteredTotal = filtered.length;
    const items = filtered.slice(offset, offset + limit);
    return { items, total, filteredTotal, offset, limit };
  }
  async getCampusScaleSummary(): Promise<CampusScaleSummary> {
    const metadata = await db.datasetMetadata.get('canonical');
    if (metadata?.profileId === CAMPUS_SCALE_PROFILE.profileId) {
      return CAMPUS_SCALE_PROFILE;
    }
    const [buildings, employees, installedJacks, activeJacks] = await Promise.all([
      db.buildings.count(),
      db.persons.count(),
      db.dataJacks.count(),
      db.dataJacks.where('status').equals('active').count()
    ]);
    const managedAssets = metadata?.managedAssetCount ?? await db.assets.count();
    const observedDevices = metadata?.observedDeviceCount ?? await db.observedDevices.count();
    return {
      profileId: metadata?.profileId ?? 'unversioned-local-dataset',
      buildings,
      employees,
      installedJacks,
      activeJacks,
      wirelessAccessPoints: 0,
      managedAssets,
      observedDevices,
      identifiableDevices: managedAssets + observedDevices,
      typicalWeekdayPersonalDevices: 9100,
      peakConnectedDevices: 6200
    };
  }
  async getAssignment(assignmentId: string): Promise<DeviceAssignment | null> {
    return (await db.deviceAssignments.get(assignmentId)) || null;
  }
  async getAssignmentsByAsset(assetId: string): Promise<DeviceAssignment[]> {
    const stored = await db.deviceAssignments.where('assetId').equals(assetId).toArray();
    return stored.length ? stored : getCanonicalAssignment(assetId);
  }
}

export class LocalInfrastructureRepository implements InfrastructureRepository {
  async getDataJack(jackId: string): Promise<DataJack | null> {
    return (await db.dataJacks.get(jackId)) || null;
  }
  async traceFromRoom(roomId: string): Promise<NetworkPath | null> {
    const paths = await db.networkPaths.where('sourceId').equals(roomId).toArray();
    return paths[0] || null;
  }
  async traceFromService(serviceId: string): Promise<NetworkPath | null> {
    const paths = await db.networkPaths.where('targetId').equals(serviceId).toArray();
    return paths[0] || null;
  }
}

export class LocalWorkRepository implements WorkRepository {
  async getWorkOrder(orderId: string): Promise<WorkOrder | null> {
    return (await db.workOrders.get(orderId)) || null;
  }
  async getMaintenanceTask(taskId: string): Promise<MaintenanceTask | null> {
    return (await db.maintenanceTasks.get(taskId)) || null;
  }
}

export class LocalIncidentRepository implements IncidentRepository {
  async getIncident(incidentId: string): Promise<Incident | null> {
    return (await db.incidents.get(incidentId)) || null;
  }
}

export class LocalVendorRepository implements VendorRepository {
  async getVendor(vendorId: string): Promise<Vendor | null> {
    return (await db.vendors.get(vendorId)) || null;
  }
  async getService(serviceId: string): Promise<Service | null> {
    return (await db.services.get(serviceId)) || null;
  }
}

export const localRepositories = {
  campus: new LocalCampusRepository(),
  inventory: new LocalInventoryRepository(),
  infrastructure: new LocalInfrastructureRepository(),
  work: new LocalWorkRepository(),
  incidents: new LocalIncidentRepository(),
  vendors: new LocalVendorRepository()
};
